import { pdf } from '@react-pdf/renderer';

import { selectAllCommentaryData } from '../../../redux/features/selectors/commentarySelectors/selectAllCommentaryData.ts';
import { Tkv2SiteValueChartsParams } from '../../referenceView/utils/useTkv2SiteValueChartsParams.ts';
import { pngHomeValueIndexTrendsChartMakerV2 } from '../../summaryV2/utils/pngHomeValueIndexTrendsChartMaker.tsx';
import { pngSiteSizeToCostPerSFChartMaker } from '../../summaryV2/utils/pngSiteSizeToCostPerSFChartMaker.tsx';
import { pngSiteSizeToSiteValueChartMaker } from '../../summaryV2/utils/pngSiteSizeToSiteValueChartMaker.tsx';
import { ExportCostApproach } from './ExportCostApproach/ExportCostApproach.tsx';
import { ExportDepreciatedCost } from './ExportDepreciatedCost.tsx';
import { ExportMarketAdjustments } from './ExportMarketAdjustments.tsx';
import { ExportMethodology } from './ExportMethodology.tsx';
import { ExportSiteExtraction } from './ExportSiteExtraction.tsx';

export const generateExportPdfBlobs = async (
  {
    mainDwelling,
    quality,
    condition,
    functionalDepreciation,
    externalDepreciation,
    additionalStructures,
    siteExtraction,
    marketAdjustments,
    costApproach,
    subjectAddressDisplay,
  }: ReturnType<typeof selectAllCommentaryData>,
  siteValueChartParams: Tkv2SiteValueChartsParams,
  hasExternalDepreciation: boolean,
  hasFunctionalDepreciation: boolean,
): Promise<{ blob: Blob; name: string }[]> => {
  const { street: subjectStreetAddress, full: subjectFullAddress } = subjectAddressDisplay;

  const siteSizeVsSiteValuePngSource = await pngSiteSizeToSiteValueChartMaker(siteValueChartParams);
  const siteSizeVsCostPngSource = await pngSiteSizeToCostPerSFChartMaker(siteValueChartParams);

  const depreciatedCostBlob = await pdf(
    <ExportDepreciatedCost
      data={{
        mainDwelling,
        quality,
        condition,
        functionalDepreciation,
        externalDepreciation,
        additionalStructures,
        subjectAddressDisplay,
      }}
      hasExternalDepreciation={hasExternalDepreciation}
      hasFunctionalDepreciation={hasFunctionalDepreciation}
    />,
  ).toBlob();

  const siteExtractionBlob = await pdf(
    <ExportSiteExtraction
      data={{ siteExtraction, subjectAddressDisplay }}
      charts={{
        siteSizeVsSiteValue: siteSizeVsSiteValuePngSource,
        siteSizeVsCost: siteSizeVsCostPngSource,
      }}
    />,
  ).toBlob();

  const marketAdjustmentsPngSource = await pngHomeValueIndexTrendsChartMakerV2(
    marketAdjustments?.chart,
  );

  const marketAdjustmentsBlob = await pdf(
    <ExportMarketAdjustments
      data={marketAdjustments}
      subjectAddress={subjectFullAddress}
      chartSrc={marketAdjustmentsPngSource}
    />,
  ).toBlob();

  const methodologyBlob = await pdf(
    <ExportMethodology subjectAddress={subjectFullAddress} />,
  ).toBlob();

  const costApproachBlob = await pdf(
    <ExportCostApproach
      data={costApproach}
      subjectAddress={subjectFullAddress}
      hasExternalDepreciation={hasExternalDepreciation}
      hasFunctionalDepreciation={hasFunctionalDepreciation}
    />,
  ).toBlob();

  return [
    { blob: depreciatedCostBlob, name: `${subjectStreetAddress} - Depreciated Cost.pdf` },
    { blob: siteExtractionBlob, name: `${subjectStreetAddress} - Site Extraction.pdf` },
    {
      blob: marketAdjustmentsBlob,
      name: `${subjectStreetAddress} - Date of Sale Adjustments.pdf`,
    },
    { blob: costApproachBlob, name: `${subjectStreetAddress} - Cost Approach.pdf` },
    { blob: methodologyBlob, name: `${subjectStreetAddress} - Methodology.pdf` },
  ];
};
